import { useEffect } from "react";

/**
 * Match descriptor for a keyboard shortcut.
 *
 *   key: KeyboardEvent.key (case-insensitive comparison except letters)
 *   mod: requires Ctrl OR Meta (mac).
 */
export interface ShortcutMatcher {
  key: string;
  mod?: boolean;
  shift?: boolean;
  alt?: boolean;
  /** When true (default), prevent default + stop propagation. */
  preventDefault?: boolean;
}

export type ShortcutHandler = (e: KeyboardEvent) => void;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (target.isContentEditable) return true;
  return false;
}

function matches(e: KeyboardEvent, m: ShortcutMatcher): boolean {
  const wantMod = m.mod ?? false;
  if (e.ctrlKey && e.metaKey) return false;
  const haveMod = e.ctrlKey || e.metaKey;
  if (wantMod !== haveMod) return false;
  if ((m.shift ?? false) !== e.shiftKey) return false;
  if ((m.alt ?? false) !== e.altKey) return false;
  return e.key.toLowerCase() === m.key.toLowerCase();
}

/**
 * Globally listen for one shortcut. Skips when typing in an input unless `allowInInput`.
 */
export function useShortcut(
  matcher: ShortcutMatcher,
  handler: ShortcutHandler,
  options: { allowInInput?: boolean; enabled?: boolean } = {},
): void {
  const { allowInInput = false, enabled = true } = options;
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !matches(e, matcher)) return;
      if (!allowInInput && isEditableTarget(e.target)) return;
      if (matcher.preventDefault !== false) {
        e.preventDefault();
        e.stopPropagation();
      }
      handler(e);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [matcher.key, matcher.mod, matcher.shift, matcher.alt, allowInInput, enabled, handler]);
}

/** Pre-built shortcut matchers for use across the app. */
export const SHORTCUTS = {
  search: { key: "k", mod: true } satisfies ShortcutMatcher,
  send: { key: "Enter", mod: true, preventDefault: false } satisfies ShortcutMatcher,
  toggleSidebar: { key: "L", mod: true, shift: true } satisfies ShortcutMatcher,
  toggleContext: { key: "\\", mod: true } satisfies ShortcutMatcher,
  newDm: { key: "n", mod: true } satisfies ShortcutMatcher,
  myTodos: { key: "m", mod: true, alt: true } satisfies ShortcutMatcher,
  settings: { key: ",", mod: true } satisfies ShortcutMatcher,
  helpDialog: { key: "?", shift: true } satisfies ShortcutMatcher,
  searchFocus: { key: "/" } satisfies ShortcutMatcher,
  esc: { key: "Escape", preventDefault: false } satisfies ShortcutMatcher,
} as const;
